import { downloadWorkbook, styleRecordSheet } from '../../utils/excelExport';

export const cargoColumns = ['Marko', 'Type', 'Qty', 'Carton Qty', 'Product', 'Product Qty', 'Product Gross Wt (kg)', 'Product Net Wt (kg)', 'Gross Weight (kg)', 'Net Weight (kg)', 'Total CBM'];
export const cartonMeasurementColumns = ['Carton Gross (kg)', 'Carton Net (kg)', 'Carton CBM'];

export function cartonMeasurement(batch, field) {
  return batch?.[field] ?? batch?.products?.reduce((sum, product) => sum + Number(product[field] || 0), 0) ?? 0;
}

export function cargoSummary(inventory) {
  const normalized = inventory.map(item => item.packaging?.length ? item : { ...item, packaging: [{ ...item, batches: [{ ...item, batch_quantity: item.total_carton_qty, products: item.products || [] }] }] });
  const rows = inventoryRows(normalized);
  const sum = (span, value) => Number(rows.reduce((total, row) => total + (span && !row[span] ? 0 : Number(value(row)) || 0), 0).toFixed(6));
  return {
    totalProducts: new Set(rows.filter(row => row.product).map(row => row.product.product_id || row.product.name)).size,
    totalPackages: sum('packSpan', row => row.pack.packaging_quantity),
    totalCartons: sum('batchSpan', row => row.batch?.batch_quantity ?? row.pack.total_carton_qty),
    totalQuantity: sum(null, row => row.product?.quantity),
    productGross: sum(null, row => row.product?.gross_weight),
    productNet: sum(null, row => row.product?.net_weight),
    cartonGross: sum('batchSpan', row => cartonMeasurement(row.batch, 'gross_weight')),
    cartonNet: sum('batchSpan', row => cartonMeasurement(row.batch, 'net_weight')),
    cartonCbm: sum('batchSpan', row => cartonMeasurement(row.batch, 'cbm')),
    totalGrossWeight: sum('packSpan', row => row.pack.gross_weight),
    totalNetWeight: sum('packSpan', row => row.pack.net_weight),
    totalCbm: sum('packSpan', row => row.pack.cbm),
  };
}

export function cargoSummaryColumns(totals) {
  return {
    Qty: totals.totalPackages, 'Carton Qty': totals.totalCartons, Product: totals.totalProducts, 'Product Qty': totals.totalQuantity,
    'Product Gross Wt (kg)': totals.productGross, 'Product Net Wt (kg)': totals.productNet,
    'Carton Gross (kg)': totals.cartonGross, 'Carton Net (kg)': totals.cartonNet, 'Carton CBM': totals.cartonCbm,
    'Gross Weight (kg)': totals.totalGrossWeight, 'Net Weight (kg)': totals.totalNetWeight, 'Total CBM': totals.totalCbm,
  };
}

export function filterInventory(inventory, query) {
  const term = query.trim().toLowerCase();
  if (!term) return inventory;
  return inventory.map(item => {
    const matches = `${item.marko} ${item.reference_number}`.toLowerCase().includes(term);
    return { ...item, packaging: item.packaging.map(pack => ({
      ...pack,
      batches: pack.batches.map(batch => ({
        ...batch, products: matches || batch.batch_number.toLowerCase().includes(term)
          ? batch.products
          : batch.products.filter(product => product.name.toLowerCase().includes(term)),
      })).filter(batch => batch.products.length).map(batch => pack.measurement_level === 'Product' && !pack.use_master_measurements ? { ...batch,
        ...Object.fromEntries(['cbm', 'net_weight', 'gross_weight'].map(field => [field, batch.products.reduce((sum, product) => sum + Number(product[field] || 0), 0)])) } : batch),
    })).filter(pack => pack.batches.length).map(pack => ({
      ...pack,
      net_weight: pack.batches.reduce((total, batch) => total + Number(batch.net_weight ?? batch.products.reduce((sum, product) => sum + Number(product.net_weight || 0), 0)), 0),
      gross_weight: pack.batches.reduce((total, batch) => total + Number(batch.gross_weight ?? batch.products.reduce((sum, product) => sum + Number(product.gross_weight || 0), 0)), 0),
    })) };
  }).filter(item => item.packaging.length);
}

export function inventoryRows(inventory) {
  return inventory.flatMap(item => {
    const rows = item.packaging.flatMap(pack => {
      const packRows = pack.batches.flatMap(batch => {
        const products = batch.products.length ? batch.products : [null];
        return products.map((product, index) => ({
          key: `${item.id}-${pack.id}-${batch.id}-${product?.id || 'empty'}`,
          item, pack, batch, product, batchSpan: index === 0 ? products.length : 0,
        }));
      });
      if (!packRows.length) packRows.push({ key: `${item.id}-${pack.id}-empty`, item, pack, batch: null, product: null, batchSpan: 1 });
      packRows.forEach((row, index) => { row.packSpan = index === 0 ? packRows.length : 0; });
      return packRows;
    });
    rows.forEach((row, index) => { row.inventorySpan = index === 0 ? rows.length : 0; });
    return rows;
  });
}

export async function exportCargoManifest(details, format = 'pdf') {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Cargo Manifest', { views: [{ state: 'frozen', ySplit: 1 }] });
  sheet.columns = ['Receiving Date', ...cargoColumns.slice(1), 'Marko', 'Status'].flatMap(header => header === 'Gross Weight (kg)' ? [...cartonMeasurementColumns, header] : [header]).map(header => ({ header, width: header === 'Product' ? 35 : 22 }));
  const merges = [];
  const rows = inventoryRows(details.inventory);
  const statusKeys = row => [...(row.batch?.container_statuses || row.item.container_statuses || ['Planning'])].sort();
  rows.forEach(({ item, pack, batch, product, inventorySpan, packSpan, batchSpan }, index) => {
    const keys = statusKeys(rows[index]);
    const sameStatus = other => other?.item === item && JSON.stringify(statusKeys(other)) === JSON.stringify(keys);
    let statusSpan = 0;
    if (!sameStatus(rows[index - 1])) {
      statusSpan = 1;
      while (sameStatus(rows[index + statusSpan])) statusSpan += 1;
    }
    const statusLabel = keys.map(key => details.statuses?.find(status => status.key === key)?.label || key).join(', ');
    const row = sheet.addRow([
      inventorySpan ? (item.inventory_date ? item.inventory_date.slice(0, 10).split('-').reverse().join('-') : '') : null,
      packSpan ? pack.packaging_type : null, packSpan ? pack.packaging_quantity : null,
      batchSpan ? batch?.batch_quantity ?? pack.total_carton_qty : null, product?.name || '', product?.quantity ?? null,
      product?.gross_weight ?? null, product?.net_weight ?? null,
      ...['gross_weight', 'net_weight', 'cbm'].map(field => batchSpan ? cartonMeasurement(batch, field) : null),
      packSpan ? pack.gross_weight : null, packSpan ? pack.net_weight : null, packSpan ? pack.cbm : null,
      inventorySpan ? item.marko : null, statusSpan ? statusLabel : null,
    ]);
    const merge = (columns, span) => {
      if (span > 1) columns.forEach(column => merges.push([row.number, column, row.number + span - 1, column]));
    };
    merge([1, 15], inventorySpan);
    merge([16], statusSpan);
    merge([2, 3, 12, 13, 14], packSpan);
    merge([4, 9, 10, 11], batchSpan);
  });
  const totals = cargoSummaryColumns({ ...cargoSummary(details.inventory), ...details.summary });
  const summary = sheet.addRow(sheet.columns.map((column, index) => index === 0 ? 'TOTAL SUMMARY' : totals[column.header] ?? null));
  styleRecordSheet(sheet, { numericColumns: new Set([3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]) });
  sheet.eachRow(row => row.eachCell({ includeEmpty: true }, cell => {
    cell.alignment = { ...cell.alignment, horizontal: 'left' };
  }));
  merges.forEach(range => sheet.mergeCells(...range));
  sheet.mergeCells(summary.number, 1, summary.number, 2);
  summary.eachCell(cell => { cell.font = { ...cell.font, bold: true }; });
  return downloadWorkbook(workbook, `customer-${details.customer.id}-cargo-manifest.${format}`);
}
